import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Global axios 401 interceptor — auto-refresh the access token once and
   * retry the failed request.
   *
   * Why this exists: the backend issues 15-minute access tokens. Without
   * an interceptor, every page becomes a ticking timebomb: 15 mins after
   * sign-in every API call silently 401s and the user sees "Failed to
   * load…" toasts with no idea they were logged out.
   *
   * Behaviour:
   *   - Only auto-refresh once per failed request (`_retry` flag prevents
   *     an infinite loop if the refresh itself returns 401).
   *   - Skip refresh for the refresh / login / register / logout calls
   *     themselves (those are the "authentication" endpoints — a 401 on
   *     them is real and should propagate to the UI).
   *   - If refresh succeeds, replay the original request transparently.
   *   - If refresh fails, clear the in-memory user and let route guards
   *     send the user back to /login.
   */
  useEffect(() => {
    const isAuthEndpoint = (url = '') =>
      /\/auth\/(me|refresh|login|register|logout)$/.test(url);

    let pendingRefresh = null; // de-dupe concurrent 401s into a single refresh

    const interceptor = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const originalReq = error?.config;
        const status = error?.response?.status;

        if (status !== 401 || !originalReq || originalReq._retry) {
          return Promise.reject(error);
        }
        if (isAuthEndpoint(originalReq.url)) {
          return Promise.reject(error);
        }

        originalReq._retry = true;
        try {
          pendingRefresh = pendingRefresh
            || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
              .finally(() => { pendingRefresh = null; });
          const refreshResponse = await pendingRefresh;
          // Hydrate the freshly-returned user into context — saves an extra
          // /me call on the way back through the retry path.
          if (refreshResponse?.data) setUser(refreshResponse.data);
          return axios(originalReq);
        } catch (refreshErr) {
          setUser(null);
          return Promise.reject(refreshErr);
        }
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [API]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, disclaimerAccepted, referralCode = null) => {
    const response = await axios.post(
      `${API}/auth/register`,
      {
        email,
        password,
        name,
        disclaimer_accepted: !!disclaimerAccepted,
        referral_code: referralCode || null,
      },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    // Always clear local state, even if the backend call fails (e.g. the
    // session was already expired, the backend was redeployed mid-session,
    // or the network blip'd). The whole point of logout is "I want to be
    // logged out" — surfacing a 401 to the user (and to Sentry) when they
    // were already effectively logged out is just noise.
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_err) {
      // Swallow — local state cleanup below is what matters to the user.
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
